import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { cartUtils, inr, wishlistUtils } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, Heart } from "lucide-react";
import Stars from "@/components/Stars";
import ProductReviews from "@/components/ProductReviews";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [saved, setSaved] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setSaved(wishlistUtils.has(r.data.id));
    }).catch(() => setP(false));
  }, [slug]);

  useEffect(() => {
    const upd = () => { if (p) setSaved(wishlistUtils.has(p.id)); };
    window.addEventListener("wishlist-updated", upd);
    return () => window.removeEventListener("wishlist-updated", upd);
  }, [p]);

  if (p === false) return <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-center"><p className="font-serif text-3xl">Piece not found.</p><Link to="/" className="link-underline mt-4 inline-block">Back home</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-stone-500">Loading…</div>;

  const outOfStock = (p.stock || 0) <= 0;
  const maxQty = Math.max(1, p.stock || 1);

  const addToCart = () => {
    if (outOfStock) {
      toast.error("This piece is currently out of stock.");
      return;
    }
    if (qty > (p.stock || 0)) {
      toast.error(`Only ${p.stock} in stock.`);
      return;
    }
    cartUtils.add(p, qty);
    toast.success(`Added ${qty} × ${p.name} to bag`);
  };

  const toggleWishlist = async () => {
    if (!user) {
      toast.info("Please log in to save to your wishlist.");
      nav("/login");
      return;
    }
    if (wlBusy) return;
    setWlBusy(true);
    try {
      if (saved) { await wishlistUtils.remove(p.id); toast.success("Removed from wishlist"); }
      else { await wishlistUtils.add(p.id); toast.success("Saved to wishlist"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update wishlist");
    } finally {
      setWlBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <Link to={`/shop/${p.category}`} className="text-xs tracking-widest uppercase text-stone-500 hover:text-stone-900">← Back to {p.category}</Link>
      <div className="mt-8 grid md:grid-cols-2 gap-12">
        <div>
          <div className="aspect-[4/5] bg-stone-100 overflow-hidden">
            <img src={p.images?.[imgIdx]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.images?.length > 1 && (
            <div className="mt-4 flex gap-3">
              {p.images.map((im, i) => (
                <button key={i} data-testid={`pdp-thumb-${i}`} onClick={() => setImgIdx(i)} className={`w-20 h-20 overflow-hidden ${i === imgIdx ? "ring-2 ring-[#1C1917]" : "opacity-60"}`}>
                  <img src={im} className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:pl-8">
          <p className="eyebrow">{p.subcategory || p.category}</p>
          <h1 data-testid="pdp-title" className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">{p.name}</h1>
          {(p.rating_count || 0) > 0 && (
            <div className="mt-3 flex items-center gap-2" data-testid="pdp-rating">
              <Stars value={p.rating_avg || 0} size={15} />
              <span className="text-sm text-stone-600">{(p.rating_avg || 0).toFixed(1)} · {p.rating_count} review{p.rating_count === 1 ? "" : "s"}</span>
            </div>
          )}
          <p data-testid="pdp-price" className="mt-4 text-2xl text-stone-800">{inr(p.price)}</p>
          <p className="mt-6 text-stone-600 leading-relaxed">{p.description}</p>

          {p.care_instructions && (
            <div className="mt-6 border-l-2 border-[#8B5A2B] pl-4">
              <p className="text-xs tracking-widest uppercase text-[#8B5A2B]">Care & Maintenance</p>
              <p data-testid="pdp-care" className="mt-2 text-sm text-stone-600 leading-relaxed whitespace-pre-line">{p.care_instructions}</p>
            </div>
          )}

          <dl className="mt-8 border-t border-stone-200 divide-y divide-stone-200 text-sm">
            {p.material && <div className="flex justify-between py-3"><dt className="text-stone-500">Material</dt><dd>{p.material}</dd></div>}
            {p.dimensions && <div className="flex justify-between py-3"><dt className="text-stone-500">Dimensions</dt><dd>{p.dimensions}</dd></div>}
            <div className="flex justify-between py-3"><dt className="text-stone-500">Availability</dt><dd data-testid="pdp-availability" className={outOfStock ? "text-stone-500" : ((p.stock || 0) <= 3 ? "text-[#8B5A2B]" : "")}>
              {outOfStock ? "Out of stock — Made-to-order available" : ((p.stock || 0) <= 3 ? `Only ${p.stock} left — order soon` : `${p.stock} in stock`)}
            </dd></div>
          </dl>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-stone-300">
              <button data-testid="pdp-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" disabled={outOfStock}><Minus size={14}/></button>
              <span data-testid="pdp-qty" className="w-10 text-center text-sm">{qty}</span>
              <button data-testid="pdp-qty-plus" onClick={() => setQty(Math.min(maxQty, qty + 1))} className="p-3" disabled={outOfStock}><Plus size={14}/></button>
            </div>
            <button data-testid="pdp-add-to-cart" onClick={addToCart} disabled={outOfStock} className={`btn-primary flex items-center gap-2 ${outOfStock ? "opacity-40 cursor-not-allowed" : ""}`}><ShoppingBag size={14}/> {outOfStock ? "Out of Stock" : "Add to Bag"}</button>
            <button
              data-testid="pdp-wishlist-toggle"
              onClick={toggleWishlist}
              disabled={wlBusy}
              aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
              className={`w-11 h-11 border flex items-center justify-center transition-colors ${saved ? "border-[#B23A3A] text-[#B23A3A]" : "border-stone-300 text-stone-700 hover:border-[#B23A3A] hover:text-[#B23A3A]"}`}
            >
              <Heart size={16} fill={saved ? "#B23A3A" : "none"} strokeWidth={1.6} />
            </button>
          </div>

          <div className="mt-10 p-6 bg-stone-100/70">
            <p className="text-xs tracking-widest uppercase text-[#8B5A2B] mb-2">Prefer it your way?</p>
            <p className="text-sm text-stone-600">Every piece can be re-crafted in your wood, size and finish. <Link to="/custom-order" className="underline">Start a custom commission →</Link></p>
          </div>
        </div>
      </div>

      <ProductReviews slug={p.slug} />
    </div>
  );
}
